import axios from 'axios';
import { updateNotifications } from 'src/api/settings';
import { useAuthContext } from 'src/auth/hooks';

// config
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });
  if (res.status !== 200) {
    return { error: true, status: res.status };
  }
  return res.data;
};
// ----------------------------------------------------------------------
export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/company',
    login: '/company/login',
    register: '/api/auth/register',
    forgotPassword: '/company/user/forget-password',
    newPassword: '/company/user/reset-password',
    getCompanyList: '/company/user/company-names',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  // getJobs: {
  //   list: '/job/v1/jobs-for-dashboard',
  // },
  // dashboard: {
  //   list: '/dashboard/impressions',
  //   logs: '/logs/v1/all',
  //   heatmap: '/job/v1/heatmap',
  // },
  user: {
    list: '/get-company-users',
    create: '/company/user/register',
    update: '/company/user/update',
    changeStatus: '/company/user/change-status',
    delete: '/company/user',
    report: '/company/users/report',
    jobs: {
      createMany: '/job/v1/create-many',
    },
  },
  role: {
    list: '/roles',
    create: '/roles/create',
    update: '/roles/update-role',
    delete: '/roles/delete-role',
    permissions: '/permissions/get-permission',
    updatePermissions: '/roles/assign-permissions',
  },
  region: {
    list: '/region',
    create: '/region/create',
    update: '/region/update',
    delete: '/region',
  },
  division: {
    list: '/division',
    create: '/division/create',
    update: '/division/update',
    delete: '/division',
  },
  office: {
    list: '/office/all',
    create: '/office/create',
    update: '/office/update',
    getUserByRoleName: '/get-users-by-role?roleName=City Manager',
    delete: '/office',
  },
  vehicle: {
    list: '/driver/vehicle/all',
    changeStatus: '/driver/vehicle/status',
  },
  campaign: {
    list: '/campaign/all',
    create: '/campaign/create',
    update: '/campaign/update',
    changeStatus: '/campaign/change-status',
    delete: '/campaign',
    report: '/campaign/report',
  },
  driver: {
    list: '/driver/all',
    create: '/driver/create',
    update: '/driver/update-driver',
    changeStatus: '/driver/status',
    delete: '/driver',
    report: '/driver/report',
  },
  profile: {
    company: '/company/update/profile',
    // user: '/canvasser/v1/profile/update',
    user: '/user/update',
  },
  sales: {
    sales_data: '/job/v1/all-jobs',
  },
  canvasser: {
    list: '/canvasser/v1',
    create: '/canvasser/v1/create',
    update: '/canvasser/v1/update',
    delete: '/canvasser/v1/delete',
  },
  revzone: {
    list: '/revzones/v1/canvasser',
    stats: 'revzones/v1/canvasser/zone/stats',
  },
  leads: {
    list: '/canvasser/lead/v1/?canvasserManagerId=',
    create: '/canvasser/lead/v1/create',
    update: '/canvasser/lead/v1/update',
    delete: '/canvasser/lead/v1/delete',
  },
  crm: {
    list: '/canvasser/lead/v1/crm/go-back/?canvasserManagerId=',
    update: '/canvasser/lead/v1/crm/update',
    delete: '/canvasser/lead/v1/crm/delete',
    change_status: '/canvasser/lead/v1/crm/change-status',
  },
  dashboard: {
    list: '/canvasser/lead/v1/crm/dashboard',
    lmsStats: '/lms/v1/dashboard/stats',
    timeSpent: '/lms/v1/watchdog/get/watchtime/userId',
  },
  settings: {
    updatePassword: '/canvasser/v1/settings/update/password',
    updateNotifications: '/canvasser/v1/settings/update/notification',
  },
  stats: {
    list: '/canvasser/lead/v1/crm/dashboard/?date=',
    goBackReminder: '/canvasser/lead/v1/reminders/go-back',
    recentActivity: '/canvasser/lead/v1/activity/recent',
    performanceGraph: '/canvasser/v1/dashboard/graph',
  },
  tracking: {
    start: '/tracking/v1/start',
    stop: '/tracking/v1/stop',
  },
  getJobs: {
    list: '/job/v1/canvasser/jobs-for-dashboard',
  },

  // new endpoints

  courses: {
    // allCoursesList: '/lms/v1/get/courses',
    allCoursesList: 'lms/v1/get/courses/by/admin',
    companyCoursesList: '/lms/v1/get/company/courses',
    enroll: '/lms/v1/create/progress',
    enrolledCourses: 'lms/v1/get/courses/enrolled/companyId',
    courseSections: 'lms/v1/get/course',
    myCoursesList: '/courses/v1/create',
    update: '/courses/v1/update',
    delete: '/courses/v1/delete',
    getCertificate: '/lms/v1/certificate',
    generateCertificate: '/lms/v1/certificate',
  },
  progress: {
    getProgress: 'lms/v1/get/progress/courseId',
    addSection: 'lms/v1/update/progress/add/section',
    addVideoProgress: '/lms/v1/update/progress/section/add/video',
    addQuizProgress: '/lms/v1/update/progress/section/add/quiz',
    timeSpent: '/lms/v1/update/section/watchtime',
  },
  s3: {
    get_secure_links: 'url/v1/secure/multiple',
  },
  logs: {
    create: '/user-activity-log/v1/create',
  },
};
